import { MouseEvent, TouchEvent, useMemo } from "react";
import moment from "moment-timezone";
import { ContiguousTimeslot, Timeslot } from "@core/features/timeslots";
import type { ProcessedSelectableOption } from "@core/features/scheduling";
import { SchedulingAvailabilityTimeSlot } from "../SchedulingAvailabilityTimeSlot";
import { SchedulingAvailabilityGridHeaderCell } from "../SchedulingAvailabilityGridHeaderCell";

export type Props = {
  formattedTime: string;
  timeSlots: Timeslot[];
  selectedTimeSlots: Timeslot[];
  contiguousTimeslots: ContiguousTimeslot[];
  handleMouseDown: (e: MouseEvent<HTMLDivElement>, timeSlot: Timeslot) => void;
  handleMouseOver: (e: MouseEvent<HTMLDivElement>, timeSlot: Timeslot) => void;
  handleTouchStart: (e: TouchEvent<HTMLDivElement>, timeSlot: Timeslot) => void;
  handleTouchMove: (e: TouchEvent<HTMLDivElement>, timeSlot: Timeslot) => void;
  handleTouchEnd: (e: TouchEvent<HTMLDivElement>, timeSlot: Timeslot) => void;
  timeIndex: number;
  /** Selectable options for 'select-options' mode (processed with required id/day) */
  selectableOptions: ProcessedSelectableOption[];
  /** IDs of currently selected options */
  selectedOptionIds: string[];
  /** Whether we're in an options-based mode (select-options or repeating) */
  isOptionsMode: boolean;
  /** Callback to set hovered option ID for group hover effect */
  onOptionHover?: (optionId: string | null) => void;
  /** Set of currently hovered option IDs (for group hover effect, includes repeating groups) */
  hoveredOptionIds?: Set<string>;
  /** Callback to set focused option ID for group focus effect */
  onOptionFocus?: (optionId: string | null) => void;
  /** Set of currently focused option IDs (for group focus effect, includes repeating groups) */
  focusedOptionIds?: Set<string>;
  /** Whether we're in repeating mode (for left border styling) */
  isRepeatingMode?: boolean;
  /** Whether this is the last row in the grid (for bottom border) */
  isLastRow?: boolean;
  /** Set of option IDs that should be tabbable (for keyboard navigation in options modes) */
  tabbableOptionIds?: Set<string>;
};

/**
 * Check if a slot matches an option's date.
 * Uses moment-timezone to properly parse the UTC date from the slot.
 */
function slotMatchesOptionDate(
  slot: Timeslot,
  option: ProcessedSelectableOption
): boolean {
  // If option has a specific date, match by date (YYYY-MM-DD)
  if (option.date) {
    const slotDate = moment.utc(slot.utc.start.dateString).format("YYYY-MM-DD");
    return slotDate === option.date;
  }
  // Otherwise match by day of week
  return slot.utc.start.dayIndex === option.day;
}

/** Check if a slot falls within a selectable option */
function findOptionForSlot(
  slot: Timeslot,
  options: ProcessedSelectableOption[]
): ProcessedSelectableOption | undefined {
  return options.find((opt) => {
    // Use UTC time since options are defined in UTC
    const slotHour = slot.utc.start.hour;
    const slotMinute = slot.utc.start.minute;

    const slotTime = slotHour * 60 + slotMinute;
    const optStartTime = opt.startHour * 60 + opt.startMinute;
    const optEndTime = opt.endHour * 60 + opt.endMinute;

    return (
      slotMatchesOptionDate(slot, opt) &&
      slotTime >= optStartTime &&
      slotTime < optEndTime
    );
  });
}

export function SchedulingAvailabilityGridTimeRow({
  formattedTime,
  timeSlots,
  selectedTimeSlots,
  contiguousTimeslots,
  handleMouseDown,
  handleMouseOver,
  handleTouchStart,
  handleTouchMove,
  handleTouchEnd,
  timeIndex,
  selectableOptions,
  selectedOptionIds,
  isOptionsMode,
  onOptionHover,
  hoveredOptionIds,
  onOptionFocus,
  focusedOptionIds,
  isRepeatingMode,
  isLastRow,
  tabbableOptionIds,
}: Props) {
  // Pre-compute option mappings for all slots in this row
  // Use a composite key (date + id) since slot.id is a weekly ID that repeats across weeks
  const slotOptionMap = useMemo(() => {
    const map = new Map<string, ProcessedSelectableOption | undefined>();
    for (const slot of timeSlots) {
      const key = `${slot.local.start.localDateISO}-${slot.id}`;
      map.set(key, findOptionForSlot(slot, selectableOptions));
    }
    return map;
  }, [timeSlots, selectableOptions]);

  return (
    <div role="row" aria-rowindex={timeIndex} className="contents">
      {/* Time label (only for full hours) */}
      <SchedulingAvailabilityGridHeaderCell
        className="border-r sticky left-0 bg-brand-50 z-30 !touch-auto"
        role="rowheader"
      >
        {!formattedTime.includes("30") ? formattedTime : ""}
      </SchedulingAvailabilityGridHeaderCell>

      {/* Time slots for each day */}
      {timeSlots.map((timeSlot, idx) => {
        const isSelected = selectedTimeSlots
          .map((slot) => slot.id)
          .includes(timeSlot.id);

        const isFirstSlotInContiguousBlock = contiguousTimeslots.some(
          (slot) => slot.startTimeSlotId === timeSlot.id
        );

        const matchingSlot = contiguousTimeslots.find(
          (slot) =>
            timeSlot.id >= slot.startTimeSlotId &&
            timeSlot.id <= slot.endTimeSlotId
        );

        // Option-related props - use composite key for lookup
        const slotKey = `${timeSlot.local.start.localDateISO}-${timeSlot.id}`;
        const option = slotOptionMap.get(slotKey);
        const isSelectableOption = option !== undefined;
        const isPartOfSelectedOption = option
          ? selectedOptionIds.includes(option.id)
          : false;

        // Determine if this slot is the first/last in its option (for focus border styling)
        // Use UTC time since options are defined in UTC
        // A slot is the "first" if it matches the option's start time
        const isFirstSlotOfOption = !!(
          option &&
          timeSlot.utc.start.hour === option.startHour &&
          timeSlot.utc.start.minute === option.startMinute
        );
        // A slot is the "last" if its end time matches the option's end time
        const isLastSlotOfOption = !!(
          option &&
          timeSlot.utc.end.hour === option.endHour &&
          timeSlot.utc.end.minute === option.endMinute
        );

        // Determine if this slot should be tabbable
        // In options mode: only the first slot of each tabbable option is tabbable
        const isTabbable =
          !isOptionsMode || // Freeform mode: all slots tabbable
          (isFirstSlotOfOption && option && tabbableOptionIds?.has(option.id));

        // Use a composite key that includes date to ensure uniqueness across multiple weeks
        // The slot.id is a weekly ID (0-335), so we need date to disambiguate
        const uniqueKey = `${timeSlot.local.start.localDateISO}-${timeSlot.id}`;

        return (
          <SchedulingAvailabilityTimeSlot
            key={uniqueKey}
            timeSlot={timeSlot}
            isSelected={isSelected}
            isFirstSlotInContiguousBlock={isFirstSlotInContiguousBlock}
            matchingSlot={matchingSlot}
            onMouseDown={handleMouseDown}
            onMouseOver={handleMouseOver}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            dayIndex={idx}
            timeIndex={timeIndex}
            isSelectableOption={isSelectableOption}
            isPartOfSelectedOption={isPartOfSelectedOption}
            isOptionsMode={isOptionsMode}
            optionId={option?.id}
            onOptionHover={onOptionHover}
            hoveredOptionIds={hoveredOptionIds}
            onOptionFocus={onOptionFocus}
            focusedOptionIds={focusedOptionIds}
            isRepeatingMode={isRepeatingMode}
            isLastRow={isLastRow}
            isTabbable={isTabbable}
            isFirstSlotOfOption={isFirstSlotOfOption}
            isLastSlotOfOption={isLastSlotOfOption}
          />
        );
      })}
    </div>
  );
}

export default SchedulingAvailabilityGridTimeRow;

